/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { LegacyRef, useRef, useState } from "react";
import Badge from "components-new/Badge";
import sass from "pages/StepPages/StepFour/IndexPage/RowData/table.module.sass";
import { IThreadList } from "pages/StepPages/StepFour/models/threadModel";
import cn from "classnames";
import Tooltip from "components-new/Tooltip";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowCell } from "images/FiltrationTickIcon.svg";
import NewCheckBox from "components-new/NewCheckBox";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";
import ConfirmNavigate from "components/ConfirmNavigate";
import { IOptionsSort } from "../IndexPage";

interface IColumnData {
	data: IThreadList[];
	sort: (sort: IOptionsSort) => void;
	stepOne: boolean | null;
	lastElement: React.MutableRefObject<HTMLElement | null>;
	scrollWrapper: React.MutableRefObject<HTMLElement | null>;
}

const SortBy: React.FC<{ label: string; links: IItemSort[]; change: (item: IItemSort) => void }> = ({
	label,
	links,
	change,
}) => {
	const [open, setOpen] = useState<boolean | undefined>(undefined);

	const container = useRef<HTMLDivElement | null>(null);
	useOutsideAlerter(container.current, () => setOpen(false));
	const toggleOpen = () => {
		if (open === undefined) {
			setOpen(true);
		} else {
			setOpen((prevState) => !prevState);
		}
	};

	return (
		<>
			<div ref={container} className={cn(sass.sort_cell, sass[`sort_cell-${open}`])} onClick={toggleOpen}>
				{label} <ArrowCell />
				{open !== undefined && (
					<div onClick={(e) => e.stopPropagation()} className={sass.sort}>
						{links.map((_) => {
							return (
								<label key={_.id} className={sass["sort-item"]}>
									<NewCheckBox
										name={_.key || "null"}
										onChange={(e) => change({ ..._, checked: e.target.checked })}
										checked={_.checked}
										scale={"xs"}
										type={"radio"}
									/>{" "}
									{_.name}
								</label>
							);
						})}
					</div>
				)}
			</div>
		</>
	);
};

interface IItemSort {
	id: number;
	name: string;
	checked: boolean;
	key: null | "object" | "activity" | "fill";
	ask?: AscType;
}
export type AscType = null | "ASC" | "DESC" | "FULL" | "PARTIAL" | "EMPTY";
interface ISortState {
	filled: IItemSort[];
	type: IItemSort[];
	sphere: IItemSort[];
}
const initial: ISortState = {
	filled: [
		{ id: 0, name: "По умолчанию", checked: true, key: null, ask: null },
		{ id: 1, name: "Не заполнено", checked: false, key: "fill", ask: "EMPTY" },
		{ id: 2, name: "Частично заполнено", checked: false, key: "fill", ask: "PARTIAL" },
		{ id: 3, name: "Заполнено", checked: false, key: "fill", ask: "FULL" },
	],
	type: [
		{ id: 0, name: "По умолчанию", checked: true, key: "object", ask: null },
		{ id: 1, name: "А-Я", checked: false, key: "object", ask: "ASC" },
		{ id: 2, name: "Я-А", checked: false, key: "object", ask: "DESC" },
	],
	sphere: [
		{ id: 0, name: "По умолчанию", checked: true, key: "activity", ask: null },
		{ id: 1, name: "А-Я", checked: false, key: "activity", ask: "ASC" },
		{ id: 2, name: "Я-А", checked: false, key: "activity", ask: "DESC" },
	],
};
const ColumnData: React.FC<IColumnData> = ({ data, stepOne, sort: parentSort, lastElement, scrollWrapper }) => {
	const navigate = useNavigate();

	const [sort, setSort] = useState<ISortState>(initial);

	const [target, setTarget] = useState<string | undefined>();

	const [navigateModal, setNavigateModal] = useState(false);

	const changeSort = (item: IItemSort, key: keyof typeof initial) => {
		parentSort({ key: item.key, ask: item.ask! });
		setSort((prevState) => ({
			...prevState,
			[key]: [...prevState[key]].map((_) => (_.id === item.id ? { ...item, checked: true } : { ..._, checked: false })),
		}));
	};

	return (
		<div className={sass.table}>
			<ConfirmNavigate
				subtitle={"Изменение данных может повлечь рассинхронизацию данных в системе"}
				toggle={navigateModal}
				close={() => {
					setTarget(undefined);
					setNavigateModal(false);
				}}
				url={stepOne ? `thread/${target}/description` : `calculate/${target}`}
			/>
			<table>
				<thead>
					<tr>
						<th />
						<th>№</th>
						<th>КОД</th>
						<th>НАИМЕНОВАНИЕ</th>
						<th>
							<SortBy change={(item) => changeSort(item, "filled")} label={"СТАТУС ЗАПОЛНЕНОСТИ"} links={sort.filled} />
						</th>
						<th>
							<SortBy change={(item) => changeSort(item, "type")} label={"ТИП ОБЪЕКТА"} links={sort.type} />
						</th>
						<th>
							<SortBy change={(item) => changeSort(item, "sphere")} label={"СФЕРА ДЕЯТЕЛЬНОСТИ"} links={sort.sphere} />
						</th>
					</tr>
				</thead>
			</table>
			<div
				data-comment-false={true}
				className={sass.scroll}
				ref={scrollWrapper as LegacyRef<HTMLDivElement> | undefined}>
				<table>
					<tbody>
						{data.map((_, index) => (
							<Tooltip
								key={index}
								position={"bottom"}
								color={"themeWhite"}
								menu={
									<div className={sass.tooltip}>
										<div className={sass.block}>
											<span className={sass.title}>Назначение</span>
											<div>
												<span dangerouslySetInnerHTML={{ __html: _.purpose }} />
											</div>
										</div>
										<div className={sass.block}>
											<span className={sass.title}>Ответственный</span>
											<span className={sass.link}>
												{_.responsible.lastName} {_.responsible.firstName} {_.responsible.middleName}
											</span>
										</div>
									</div>
								}>
								<tr
									key={_.id}
									onClick={() => {
										if (_.repeatable) {
											setTarget(stepOne ? `${_.id}` : `${_.id}${_.damageExists ? "/result" : ""}`);
											setNavigateModal(true);
										} else {
											stepOne
												? navigate(`thread/${_.id}/description`)
												: navigate(`calculate/${_.id}${_.damageExists ? "/result" : ""}`);
										}
									}}>
									<td>
										<div
											className={cn(
												sass.status,
												stepOne && _.threatExists && !_.repeatable
													? sass.status_allow
													: _.categoryExists && !_.repeatable
													? sass.status_succcess
													: "",
												!stepOne && _.damageExists && !_.repeatable
													? sass.status_succcess
													: !stepOne && !_.damageExists && !_.repeatable && sass.status_allow,
												_.repeatable && sass.status_purple,
											)}
										/>
									</td>
									<td>{index + 1}</td>
									<td>{_.code}</td>
									<td>{_.name}</td>
									{stepOne ? (
										<td>
											{_.status && _.status.toLowerCase() === "partial" ? (
												<Badge type={"warn"}>Частично заполнено</Badge>
											) : _.status && _.status.toLowerCase() === "empty" ? (
												<Badge type={"error"}>Не заполнено</Badge>
											) : (
												<Badge type={"success"}>Заполнено</Badge>
											)}
										</td>
									) : (
										<td>
											{_.damageStatus && _.damageStatus.toLowerCase() === "partial" ? (
												<Badge type={"warn"}>Частично заполнено</Badge>
											) : _.damageStatus && _.damageStatus.toLowerCase() === "empty" ? (
												<Badge type={"error"}>Не заполнено</Badge>
											) : (
												<Badge type={"success"}>Заполнено</Badge>
											)}
										</td>
									)}
									<td>{_.type.name}</td>
									<td>{_.activityArea?.name || <span dangerouslySetInnerHTML={{ __html: "&mdash;" }} />}</td>
								</tr>
							</Tooltip>
						))}
						<tr ref={lastElement as LegacyRef<HTMLTableRowElement> | undefined} />
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default ColumnData;
